import type { Library } from './media-views';

/** Fichier de la médiathèque tel que `ListMedia` le sert (ADR-106). */
export interface FileEntry {
  id: string;
  title: string;
  file_name: string;
  media_type: string;
  size_bytes: number;
  width?: number;
  height?: number;
  page_count?: number;
  tags: string[];
  description?: string;
  scan_status?: string;
  folder_id: string | null;
  created_at: string;
}

/** Libellés d'une médiathèque ; `e` accorde les participes (une image déposée, un document déposé). */
export const LABELS: Record<Library, { title: string; one: string; many: string; e: string; accept: string; hint: string; search: string; description: string; system: string; open: string }> = {
  images: {
    title: 'Images',
    one: 'image',
    many: 'images',
    e: 'e',
    accept: 'image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif,image/gif,image/tiff,.heic,.heif',
    hint: 'JPEG, PNG, WebP, HEIC · 25 Mo',
    search: 'Rechercher dans la médiathèque',
    description: 'Déposez plusieurs fichiers d’un coup, rangez-les par glisser-déposer : le rangement ne change aucun lien ni aucune version.',
    system: 'Dossier système : les dépôts automatiques (tablette, documents générés) y arrivent d’eux-mêmes ; il ne se renomme ni ne se supprime.',
    open: 'Propriétés',
  },
  documents: {
    title: 'Documents',
    one: 'document',
    many: 'documents',
    e: '',
    accept: 'application/pdf,.docx,.xlsx,.pptx,.odt,.ods,.csv,.txt,image/*',
    hint: 'PDF, Word, Excel, images · 100 Mo',
    search: 'Rechercher dans la médiathèque',
    description: 'Un PDF s’ouvre dans le panneau, un document Office y est lu par son PDF. Rangez par glisser-déposer : le rangement ne change aucun lien.',
    system: 'Dossier système : les bons signés et les photos de livraison prises sur la tablette y arrivent d’eux-mêmes. Un PDF s’ouvre dans le panneau, un document Office y est lu par son PDF.',
    open: 'Aperçu',
  },
};

export const sizeLabel = (bytes: number): string => (bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toLocaleString('fr', { maximumFractionDigits: 1 })} Mo` : `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('fr')} Ko`);
export const dimensionsLabel = (file: Pick<FileEntry, 'width' | 'height'>): string | undefined => (file.width === undefined || file.height === undefined ? undefined : `${file.width.toLocaleString('fr')} × ${file.height.toLocaleString('fr')}`);
export const extensionOf = (file: Pick<FileEntry, 'file_name' | 'media_type'>): string => (file.file_name.includes('.') ? file.file_name.split('.').pop()!.slice(0, 4).toUpperCase() : file.media_type.split('/').pop()!.slice(0, 4).toUpperCase());
export const dateLabel = (iso: string): string => new Date(iso).toLocaleDateString('fr', { day: '2-digit', month: '2-digit', year: 'numeric' });
export const dateTimeLabel = (iso: string): string => `${dateLabel(iso)} ${new Date(iso).toLocaleTimeString('fr', { hour: '2-digit', minute: '2-digit' })}`;
export const countOf = (count: number, one: string, many: string): string => `${count.toLocaleString('fr')} ${count > 1 ? many : one}`;

/** Famille d'un document : pastille, couleur (jeton de ton) et format affiché. */
export function documentKind(file: Pick<FileEntry, 'file_name' | 'media_type'>): { tone: 'danger' | 'info' | 'ok' | 'warning' | 'neutral'; format: string } {
  const type = file.media_type;
  if (type === 'application/pdf') return { tone: 'danger', format: 'PDF' };
  if (/wordprocessingml|msword|opendocument\.text/.test(type)) return { tone: 'info', format: 'Word (PDF servi)' };
  if (/spreadsheetml|ms-excel|opendocument\.spreadsheet|text\/csv/.test(type)) return { tone: 'ok', format: 'Excel (PDF servi)' };
  if (type.startsWith('image/')) return { tone: 'warning', format: 'Photo' };
  return { tone: 'neutral', format: extensionOf(file) };
}

export const SCAN_LABELS: Record<string, string> = { clean: 'Aucune menace détectée', pending: 'Analyse en attente', infected: 'Menace détectée', failed: 'Analyse impossible' };

/** Usage d'un dépôt (spec 192 §5) : il choisit les types admis, la taille et les variantes calculées. */
export function purposeFor(library: Library, systemKey: string | null | undefined): string {
  if (library === 'documents') return 'document';
  if (systemKey === 'delivery_proofs') return 'field_photo';
  if (systemKey === 'logos') return 'avatar';
  return 'product_image';
}

/** Octets d'un fichier choisi : `arrayBuffer` quand il existe, sinon `FileReader`. */
function bytesOf(file: Blob): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('Lecture du fichier impossible.'));
    reader.readAsArrayBuffer(file);
  });
}

export async function sha256(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(await bytesOf(file)));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
